import { useState, useMemo, useEffect } from "react";
import { Users, Store, User, User2Icon } from "lucide-react";

import { SectionCard } from "../../Component/AdminComponents/SectionCard";
import AgentsView from "../Main/Admin/AgentsView";
import OperationsView from "../Main/Admin/OperationsView";
import CustomerList from "../Main/Retailer/CustomerList";
import RetailerUsersView from "../Main/Admin/RetailerUsersView";
// import CustomersView from "../Main/Admin/CustomersView";

export default function CompanyUsers({ entityType, entityId, isRetailerMobile }) {
  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
  const userRole = authUser?.role; // logged-in user role

  const [activeTab, setActiveTab] = useState(null);

  // 🔐 Logged-in role → allowed tabs
  const ROLE_TABS = {
    1: ["agents", "retailers", "promoters", "customers"], // ✅ Admin
    2: ["agents", "retailers", "promoters", "customers"], // ✅ Company
    3: ["promoters", "customers"], 
    4: ["retailers", "customers"],                         // Agent
    5: ["promoters", "customers"],                         // Retailer
    5: ["customers"],                         // Retailer_user
  };


  // 🧩 Entity being viewed → child hierarchy
  const ENTITY_TABS = {
    company: ["agents", "retailers", "customers"],
    agent: ["retailers", "customers"],
    retailer: ["promoters", "customers"], // ✅
    retailer_user: ["customers"], // ✅
  };

  // 🧱 All possible tabs
  const ALL_TABS = [
    { key: "agents", label: "Agents", icon: Users },
    { key: "retailers", label: "Retailers", icon: Store },
    { key: "promoters", label: "Promoters", icon: User2Icon },
    { key: "customers", label: "Customers", icon: User },
  ];

  // ✅ Final visible tabs = role ∩ entity
  const visibleTabs = useMemo(() => {
    const allowedByRole = ROLE_TABS[userRole] || [];
    const allowedByEntity = ENTITY_TABS[entityType] || [];

    return ALL_TABS.filter(
      (tab) =>
        allowedByRole.includes(tab.key) &&
        allowedByEntity.includes(tab.key)
    );
  }, [userRole, entityType]);

  // ✅ Auto-select default tab
  useEffect(() => {
    if (!activeTab && visibleTabs.length > 0) {
      // 🔥 When viewing Agent → default Retailers
      if (
        entityType === "agent" &&
        visibleTabs.some((t) => t.key === "retailers")
      ) {
        setActiveTab("retailers");
      } else {
        setActiveTab(visibleTabs[0].key);
      }
    }
  }, [visibleTabs, activeTab, entityType]);

  if (!visibleTabs.length) {
    return (
      <SectionCard title="Users" isRetailerMobile={isRetailerMobile} >
        <p className="text-sm text-slate-500">
          No users available for this entity.
        </p>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Users" isRetailerMobile={isRetailerMobile}>
      {/* Tabs */}
     <div
        className={`mb-4 flex gap-2 pb-3 overflow-x-auto
        ${
          isRetailerMobile
            ? "border-b border-white/20 scrollbar-none"
            : "border-b border-blue-100"
        }`}
      >
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.key;

          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition whitespace-nowrap
                ${
                  isRetailerMobile
                    ? active
                      ? "bg-white/20 text-black border border-white/30"
                      : "bg-white/10 text-black/70 border border-white/20 hover:bg-white/20"
                    : active
                    ? "bg-blue-600 text-white"
                    : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                }
              `}
            >
              <Icon
                className={`h-4 w-4 ${
                  isRetailerMobile ? "text-black/80" : ""
                }`}
              />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === "agents" && (
        <AgentsView
          entityType={entityType}
          entityId={entityId}
        />
      )}
      {activeTab === "retailers" && 
      <OperationsView  
      entityType={entityType}
      entityId={entityId}/>
      }


       {activeTab === "promoters" && 
      <RetailerUsersView
      entityType={entityType}
      entityId={entityId}/>
      }
     {activeTab === "customers" && (
        <CustomerList
          entityType={entityType}
          entityId={entityId}
        />
      )}

    </SectionCard>
  );
}
